import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Inbox, SearchX, XCircle } from './icons';
import { Icon } from './Icon';
import { WS_COLOR, WS_STATUS } from './tokens';

export type EmptyStateKind = 'empty' | 'no-matches' | 'error' | 'loading';

export interface EmptyStateProps {
  kind: EmptyStateKind;
  title?: string;
  description?: ReactNode;
  /** The next action: a button. Required for `empty` and `error` in practice. */
  action?: ReactNode;
}

const DEFAULT_TITLE: Record<EmptyStateKind, string> = {
  empty: 'Nothing here yet',
  'no-matches': 'No matches for these filters',
  error: "Couldn't load this",
  loading: 'Loading',
};

/**
 * THE empty state — one anatomy for four situations (DESIGN.md), replacing the three
 * hand-built designs the inventory found (grey inbox icon, grey tile with a call to action,
 * colour emoji). Empty inventory, no filter matches, a failed load and a load in progress
 * differ by content and one restrained icon, never by layout.
 */
export function EmptyState({ kind, title, description, action }: EmptyStateProps) {
  if (kind === 'loading') {
    return (
      <Stack spacing={2} sx={{ p: 6 }} role="status" aria-live="polite" aria-busy="true">
        <Skeleton variant="rounded" height={12} width="40%" />
        <Skeleton variant="rounded" height={12} width="85%" />
        <Skeleton variant="rounded" height={12} width="65%" />
        <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
          {title ?? DEFAULT_TITLE.loading}
        </Typography>
      </Stack>
    );
  }
  const glyph = kind === 'no-matches' ? SearchX : kind === 'error' ? XCircle : Inbox;
  const iconColor = kind === 'error' ? WS_STATUS.error.ink : WS_COLOR.textMuted;
  return (
    <Stack
      spacing={2}
      alignItems="flex-start"
      role={kind === 'error' ? 'alert' : undefined}
      sx={{ p: 6, maxWidth: 560 }}
    >
      <Box aria-hidden sx={{ color: iconColor, display: 'flex' }}>
        <Icon icon={glyph} size={24} />
      </Box>
      <Typography variant="h2" component="h2" sx={{ fontSize: 16, lineHeight: '24px' }}>
        {title ?? DEFAULT_TITLE[kind]}
      </Typography>
      {description ? (
        <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
          {description}
        </Typography>
      ) : null}
      {action ? <Box sx={{ pt: 1 }}>{action}</Box> : null}
    </Stack>
  );
}
